import React, { useState, useMemo } from "react";
import { Search, Calendar, FileDown } from "lucide-react";
import DashboardLayout from "../components/layout/index";

const SAMPLE_ROWS = [
  {
    id: 1,
    doctorName: "Dr. Reksona Pandey",
    department: "Cardiology",
    patientName: "Triupti Shukla",
    patientId: "TEST000998888",
    consultationDate: "22/01/2026",
    consultationTime: "11:20 - 11:30",
    consultationType: "Video",
    payableAmount: 100,
    paymentStatus: "PAID",
    paymentDate: "02/02/2026",
    transactionId: "1232432",
  },
  {
    id: 2,
    doctorName: "Dr. Anya Sharma",
    department: "Neurology",
    patientName: "Aarav Mehta",
    patientId: "TEST000998888",
    consultationDate: "22/01/2026",
    consultationTime: "11:20 - 11:30",
    consultationType: "In-person",
    payableAmount: 100,
    paymentStatus: "PENDING",
    paymentDate: "",
    transactionId: "",
  },
  {
    id: 3,
    doctorName: "Dr. Rohan Patel",
    department: "Gastroenterology",
    patientName: "Saanvi Kapoor",
    patientId: "TEST000998888",
    consultationDate: "22/01/2026",
    consultationTime: "11:20 - 11:30",
    consultationType: "In-clinic Video",
    payableAmount: 100,
    paymentStatus: "PENDING",
    paymentDate: "",
    transactionId: "",
  },
  {
    id: 4,
    doctorName: "Dr. Diya Menon",
    department: "Oncology",
    patientName: "Rohan Verma",
    patientId: "TEST000998888",
    consultationDate: "22/01/2026",
    consultationTime: "11:20 - 11:30",
    consultationType: "Video",
    payableAmount: 100,
    paymentStatus: "PENDING",
    paymentDate: "",
    transactionId: "",
  },
  {
    id: 5,
    doctorName: "Dr. Arjun Singh",
    department: "Pediatrics",
    patientName: "Nisha Reddy",
    patientId: "TEST000998888",
    consultationDate: "22/01/2026",
    consultationTime: "11:20 - 11:30",
    consultationType: "In-clinic Video",
    payableAmount: 100,
    paymentStatus: "PAID",
    paymentDate: "12/02/2026",
    transactionId: "1232432",
  },
];

const PAGE_SIZE = 10;

const statusPillClasses = (status) => {
  if (status === "PAID") {
    return "bg-green-100 text-green-700";
  }
  if (status === "PENDING") {
    return "bg-orange-100 text-orange-700";
  }
  return "bg-gray-100 text-gray-600";
};

function CurebayPaymentInvoice() {
  const [doctorNameFilter, setDoctorNameFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [doctorTypeFilter, setDoctorTypeFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    return SAMPLE_ROWS.filter((row) => {
      const matchDoctor =
        !doctorNameFilter ||
        row.doctorName.toLowerCase().includes(doctorNameFilter.toLowerCase());
      const matchDept =
        !departmentFilter ||
        row.department.toLowerCase().includes(departmentFilter.toLowerCase());
      // doctorTypeFilter is placeholder for now (API-driven in future)
      return matchDoctor && matchDept;
    });
  }, [doctorNameFilter, departmentFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleRows = filteredRows.slice(startIndex, startIndex + PAGE_SIZE);

  const handleExport = () => {
    // Placeholder – wire to real export API later
    // For now just give user feedback
    // eslint-disable-next-line no-alert
    alert("Export to Excel will be implemented with backend API.");
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-white p-4 md:p-6 lg:p-8">
        <h1 className="text-xl md:text-2xl font-semibold text-[#004276] mb-6">
          Payment Invoice
        </h1>

        {/* Filters */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-6">
          <h2 className="text-base font-semibold text-[#004276] mb-4">
            Search & filter
          </h2>
          <div className="flex flex-wrap items-end gap-4 lg:gap-5">
            <div className="w-full sm:w-[200px] lg:w-[220px]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Doctor Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter Doctor Name"
                  value={doctorNameFilter}
                  onChange={(e) => {
                    setDoctorNameFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white pl-4 pr-10 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="w-full sm:w-[200px] lg:w-[220px]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Department
              </label>
              <input
                type="text"
                placeholder="Choose Department"
                value={departmentFilter}
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
              />
            </div>

            <div className="w-full sm:w-[160px] lg:w-[180px]">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Type of Doctor
              </label>
              <select
                value={doctorTypeFilter}
                onChange={(e) => setDoctorTypeFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
              >
                <option value="All">All</option>
                <option value="Video">Video</option>
                <option value="In-person">In-person</option>
                <option value="In-clinic Video">In-clinic Video</option>
              </select>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1.5 sm:sr-only">
                Date range
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-[120px]">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
                  />
                </div>
                <span className="text-gray-400 font-medium">to</span>
                <div className="relative flex-1 min-w-[120px]">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="w-full sm:w-auto sm:ml-auto">
              <button
                type="button"
                onClick={handleExport}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#004276] text-white text-sm font-medium shadow-sm hover:bg-[#00325a] focus:outline-none focus:ring-2 focus:ring-[#004276] focus:ring-offset-2 transition-colors"
              >
                <FileDown className="w-4 h-4" />
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-[#F8FAFC]">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Doctor Name
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Patient Name
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Consultation Date
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Consultation Type
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Payable Amount
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Payment Date
                  </th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">
                    Transaction Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-6 text-center text-gray-500"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 align-top">
                        <div className="font-medium text-gray-900">
                          {row.doctorName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.department}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="font-medium text-gray-900">
                          {row.patientName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.patientId}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-gray-700">
                        <div>{row.consultationDate}</div>
                        <div className="text-xs text-gray-500">
                          {row.consultationTime}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-gray-700">
                        {row.consultationType}
                      </td>
                      <td className="px-6 py-4 align-top text-gray-700">
                        Rs. {row.payableAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusPillClasses(
                            row.paymentStatus
                          )}`}
                        >
                          {row.paymentStatus === "PAID" ? "Paid" : "Pending"}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-top text-gray-700">
                        {row.paymentDate || "-"}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-[#004276]">
                        {row.transactionId ? (
                          <button
                            type="button"
                            className="underline hover:text-[#00325a]"
                          >
                            View Invoice
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`px-2 py-1 ${
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#004276] hover:underline"
                }`}
              >
                &lt; Prev
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx + 1}
                    type="button"
                    onClick={() => setPage(idx + 1)}
                    className={`h-8 min-w-[2rem] rounded-md border text-xs ${
                      currentPage === idx + 1
                        ? "border-[#004276] text-[#004276]"
                        : "border-transparent text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                className={`px-2 py-1 ${
                  currentPage === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#004276] hover:underline"
                }`}
              >
                Next &gt;
              </button>
            </div>
            <div>
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + PAGE_SIZE, filteredRows.length)} of{" "}
              {filteredRows.length} entries
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CurebayPaymentInvoice;

